/**
 * CONSAI Business App - Payment Page
 * Handles subscription and report purchase payments via Razorpay
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Crown, FileText, Check, Tag, Loader2, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Payment = () => {
  const { language } = useLanguage();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherApplied, setVoucherApplied] = useState(null);
  const [applyingVoucher, setApplyingVoucher] = useState(false);
  
  const paymentType = searchParams.get('type') || 'report';

  useEffect(() => {
    fetchPaymentConfig();
    loadRazorpayScript();
  }, []);

  const fetchPaymentConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/config`, {
        credentials: 'include'
      });
      // Read response body as text first to avoid "body already read" error
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid response from server');
      }
      setPaymentConfig(data);
    } catch (error) {
      toast.error(language === 'hi' ? 'कॉन्फिग लोड करने में त्रुटि' : 'Error loading config');
    } finally {
      setLoading(false);
    }
  };

  const loadRazorpayScript = () => {
    // Remove any existing Razorpay script to ensure fresh load
    const existingScript = document.querySelector('script[src*="razorpay"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = document.createElement('script');
    // Add cache-busting timestamp to ensure fresh Razorpay script
    script.src = `https://checkout.razorpay.com/v1/checkout.js?v=${Date.now()}`;
    script.async = true;
    document.body.appendChild(script);
  };

  const getPrice = () => {
    if (!paymentConfig) return 0;
    if (paymentType === 'report') {
      // Only active premium plan gets ₹899, everyone else pays ₹999
      const isPremium = user?.current_plan === 'premium';
      return isPremium ? paymentConfig.plans.report_premium.price : paymentConfig.plans.report_starter.price;
    }
    if (paymentType === 'starter') return paymentConfig.plans.starter.price;
    if (paymentType === 'premium') return paymentConfig.plans.premium.price;
    return 0;
  };

  const getPaymentTypeKey = () => {
    if (paymentType === 'report') return 'report_purchase';
    if (paymentType === 'starter') return 'starter_subscription';
    if (paymentType === 'premium') return 'premium_subscription';
    return paymentType;
  };

  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) return;
    
    setApplyingVoucher(true);
    try {
      const res = await fetch(`${API_URL}/api/vouchers/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: voucherCode,
          payment_type: paymentType,
          amount: getPrice()
        })
      });
      
      // Read response body as text first to avoid "body already read" error
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('Invalid response from server');
      }
      
      if (data.valid) {
        setVoucherApplied(data);
        toast.success(data.message);
      } else {
        toast.error(data.message);
        setVoucherApplied(null);
      }
    } catch (error) {
      toast.error(language === 'hi' ? 'वाउचर लागू करने में त्रुटि' : 'Error applying voucher');
    } finally {
      setApplyingVoucher(false);
    }
  };

  const handleRemoveVoucher = () => {
    setVoucherCode('');
    setVoucherApplied(null);
  };

  const handlePayment = async () => {
    setProcessing(true);
    
    try {
      const isSubscription = paymentType === 'starter' || paymentType === 'premium';
      
      if (isSubscription) {
        // Create subscription
        const subRes = await fetch(`${API_URL}/api/payments/create-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            payment_type: paymentType
          })
        });
        
        // Read response body as text first to avoid "body already read" error
        const subText = await subRes.text();
        let subData;
        try {
          subData = JSON.parse(subText);
        } catch (e) {
          throw new Error('Invalid response from server');
        }
        
        if (!subRes.ok) {
          throw new Error(subData.detail || 'Failed to create subscription');
        }
        
        // Debug: Log the key being used
        console.log('Razorpay Key ID:', subData.key_id);
        console.log('Razorpay Mode:', subData.key_id?.startsWith('rzp_live') ? 'LIVE' : 'TEST');
        
        // Open Razorpay subscription checkout
        const options = {
          key: subData.key_id,
          subscription_id: subData.razorpay_subscription_id,
          name: 'CONSAI Business',
          description: `${paymentType.charAt(0).toUpperCase() + paymentType.slice(1)} Plan - Monthly Subscription`,
          handler: async (response) => {
            try {
              console.log('Razorpay subscription response:', response);
              const verifyRes = await fetch(`${API_URL}/api/payments/verify-subscription`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  razorpay_subscription_id: response.razorpay_subscription_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature
                })
              });
              
              const verifyText = await verifyRes.text();
              let verifyData;
              try {
                verifyData = JSON.parse(verifyText);
              } catch (e) {
                throw new Error('Invalid verification response');
              }
              console.log('Verify response:', verifyData);
              
              if (verifyData.success) {
                toast.success(language === 'hi' ? 'सब्सक्रिप्शन सक्रिय!' : 'Subscription activated!');
                await refreshUser();
                // Redirect to appropriate thank you page
                if (paymentType === 'starter') {
                  navigate('/thank-you/starter');
                } else if (paymentType === 'premium') {
                  navigate('/thank-you/premium');
                } else {
                  navigate(verifyData.redirect || '/dashboard');
                }
              } else {
                toast.error(verifyData.detail || (language === 'hi' ? 'सत्यापन विफल' : 'Verification failed'));
                setProcessing(false);
              }
            } catch (error) {
              console.error('Verification error:', error);
              toast.error(language === 'hi' ? 'सत्यापन में त्रुटि' : 'Verification error');
              setProcessing(false);
            }
          },
          prefill: {
            name: user?.name || '',
            email: user?.email || '',
            contact: user?.mobile_number || ''
          },
          theme: {
            color: '#021945'
          },
          modal: {
            ondismiss: () => {
              setProcessing(false);
            }
          }
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.open();
        
      } else {
        // One-time payment (report purchase)
        const orderRes = await fetch(`${API_URL}/api/payments/create-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            payment_type: getPaymentTypeKey(),
            voucher_code: voucherApplied?.voucher?.code || null
          })
        });
        
        // Read response body as text first to avoid "body already read" error
        const orderText = await orderRes.text();
        let orderData;
        try {
          orderData = JSON.parse(orderText);
        } catch (e) {
          throw new Error('Invalid response from server');
        }
        
        if (!orderRes.ok) {
          throw new Error(orderData.detail || 'Failed to create order');
        }
        
        // Debug: Log the key being used
        console.log('Razorpay Key ID:', orderData.key_id);
        console.log('Razorpay Mode:', orderData.key_id?.startsWith('rzp_live') ? 'LIVE' : 'TEST');
        
        // Open Razorpay checkout
        const options = {
          key: orderData.key_id,
          amount: orderData.amount,
          currency: orderData.currency,
          name: 'CONSAI Business',
          description: 'Business Growth Report',
          order_id: orderData.order_id,
          handler: async (response) => {
            try {
              const verifyRes = await fetch(`${API_URL}/api/payments/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  payment_type: getPaymentTypeKey()
                })
              });
              
              const verifyText = await verifyRes.text();
              let verifyData;
              try {
                verifyData = JSON.parse(verifyText);
              } catch (e) {
                throw new Error('Invalid verification response');
              }
              
              if (verifyData.success) {
                toast.success(language === 'hi' ? 'भुगतान सफल!' : 'Payment successful!');
                await refreshUser();
                // Redirect to report thank you page with amount
                const amountInRupees = (getFinalAmount() / 100).toFixed(0);
                navigate(`/thank-you/report?amount=${amountInRupees}`);
              } else {
                toast.error(language === 'hi' ? 'भुगतान सत्यापन विफल' : 'Payment verification failed');
              }
            } catch (error) {
              toast.error(language === 'hi' ? 'सत्यापन में त्रुटि' : 'Verification error');
            }
          },
          prefill: {
            name: user?.name || '',
            email: user?.email || '',
            contact: user?.mobile_number || ''
          },
          theme: {
            color: '#021945'
          },
          modal: {
            ondismiss: () => {
              setProcessing(false);
            }
          }
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.open();
      }
      
    } catch (error) {
      toast.error(error.message || (language === 'hi' ? 'भुगतान शुरू करने में त्रुटि' : 'Error initiating payment'));
      setProcessing(false);
    }
  };

  const getFinalAmount = () => {
    if (voucherApplied) return voucherApplied.final_amount;
    return getPrice();
  };

  const formatPrice = (amount) => `₹${(amount / 100).toLocaleString('en-IN')}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#021945]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold font-manrope text-slate-900">
            {language === 'hi' ? 'भुगतान' : 'Payment'}
          </h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto p-4 md:p-8">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="font-manrope flex items-center gap-2">
                {paymentType === 'report' ? (
                  <FileText className="h-5 w-5 text-[#d7263d]" />
                ) : (
                  <Crown className="h-5 w-5 text-amber-500" />
                )}
                {paymentType === 'report' 
                  ? (language === 'hi' ? 'बिज़नेस ग्रोथ रिपोर्ट' : 'Business Growth Report')
                  : `${paymentType.charAt(0).toUpperCase() + paymentType.slice(1)} ${language === 'hi' ? 'प्लान' : 'Plan'}`
                }
              </CardTitle>
              <CardDescription className="">
                {paymentType === 'report' 
                  ? (language === 'hi' 
                    ? 'McKinsey-स्टाइल AI-जनरेटेड बिज़नेस कंसल्टिंग रिपोर्ट' 
                    : 'McKinsey-style AI-generated business consulting report')
                  : (language === 'hi' ? 'मासिक सब्सक्रिप्शन' : 'Monthly subscription')
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {paymentType === 'report' && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">
                    {language === 'hi' ? 'शामिल है:' : 'Includes:'}
                  </p>
                  <ul className="space-y-2">
                    {[
                      language === 'hi' ? 'विस्तृत बिज़नेस एनालिसिस' : 'Detailed business analysis',
                      language === 'hi' ? 'AI-जनरेटेड ग्रोथ प्लान' : 'AI-generated growth plan',
                      language === 'hi' ? '90-दिन एक्शन प्लान' : '90-day action plan',
                      language === 'hi' ? 'PDF डाउनलोड' : 'PDF download',
                      language === 'hi' ? '30 दिन प्रीमियम फीचर्स मुफ्त' : '30 days premium features FREE'
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="h-4 w-4 text-green-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">
                    {language === 'hi' ? 'मूल मूल्य' : 'Original Price'}
                  </span>
                  <span className={`font-semibold ${voucherApplied ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                    {formatPrice(getPrice())}
                  </span>
                </div>
                
                {voucherApplied && (
                  <div className="flex justify-between items-center mb-2 text-green-600">
                    <span className="flex items-center gap-1">
                      <Tag className="h-4 w-4" />
                      {language === 'hi' ? 'छूट' : 'Discount'}
                    </span>
                    <span className="font-semibold">
                      -{formatPrice(voucherApplied.discount_amount)}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="text-lg font-bold text-slate-900 font-manrope">
                    {language === 'hi' ? 'कुल' : 'Total'}
                  </span>
                  <span className="text-2xl font-bold text-[#021945]">
                    {formatPrice(getFinalAmount())}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Form */}
          <div className="space-y-4">
            {/* Voucher Code */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-manrope flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  {language === 'hi' ? 'वाउचर कोड' : 'Voucher Code'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {voucherApplied ? (
                  <div className="flex items-center justify-between bg-green-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-100 text-green-700">
                        {voucherApplied.voucher.code}
                      </Badge>
                      <span className="text-sm text-green-700">
                        {language === 'hi' ? 'लागू' : 'Applied'}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveVoucher}
                      className="text-red-600 hover:text-red-700"
                    >
                      {language === 'hi' ? 'हटाएं' : 'Remove'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder={language === 'hi' ? 'कोड दर्ज करें' : 'Enter code'}
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                      className=""
                      data-testid="voucher-input"
                    />
                    <Button
                      onClick={handleApplyVoucher}
                      disabled={!voucherCode.trim() || applyingVoucher}
                      className="bg-[#021945] hover:bg-[#021945]/90"
                      data-testid="apply-voucher-btn"
                    >
                      {applyingVoucher ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        language === 'hi' ? 'लागू करें' : 'Apply'
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pay Button */}
            <Card>
              <CardContent className="pt-6">
                <Button
                  onClick={handlePayment}
                  disabled={processing}
                  className="w-full h-12 bg-[#d7263d] hover:bg-[#d7263d]/90 text-white text-lg"
                  data-testid="pay-btn"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      {language === 'hi' ? 'प्रोसेसिंग...' : 'Processing...'}
                    </>
                  ) : (
                    <>
                      {language === 'hi' ? 'भुगतान करें' : 'Pay'} {formatPrice(getFinalAmount())}
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500">
                  <Shield className="h-4 w-4" />
                  <span className="">
                    {language === 'hi' 
                      ? 'Razorpay द्वारा सुरक्षित भुगतान' 
                      : 'Secure payment powered by Razorpay'}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Payment;
